import OlMap from 'ol/Map';
import View from 'ol/View';
import { defaults as defaultControls, Zoom } from 'ol/control';
import Draw, { createBox } from 'ol/interaction/Draw';
import type { Geometry } from 'ol/geom';
import type { Type as GeometryType } from 'ol/geom/Geometry';
import { Circle as CircleGeom, Point, Polygon } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import { boundingExtent, createEmpty, extend as extendExtent, isEmpty } from 'ol/extent';
import type { Extent } from 'ol/extent';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import { Fill, Icon, Stroke, Style, Circle as CircleStyle, Text } from 'ol/style';
import type { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';
import type BaseLayer from 'ol/layer/Base';

import {
  CV_MAHARASHTRA_BOUNDARY_KEY,
  CV_MAHARASHTRA_DIM_KEY,
  INDIA_MAP_EXTENT,
  createMaharashtraBoundaryLayer,
  createMaharashtraOutsideDimLayer,
} from '../canvas/maps/canvas-map-basemap.utils';

const DEFAULT_CENTER_LON_LAT: [number, number] = [78.9629, 20.5937];
const DEFAULT_ZOOM = 4;
const MIN_ZOOM = 3;
const MAX_ZOOM = 18;
const OGC_FEATURE_KEY = 'ogcFeature';

export type OgcDrawMode = 'polygon' | 'circle' | 'rectangle';

/** Bbox is OGC CRS84: west, south, east, north. Center is [lat, lng]. */
export interface OgcDrawnGeometry {
  type: OgcDrawMode;
  bbox: [number, number, number, number];
  coordinates?: [number, number][];
  center?: [number, number];
  radius?: number;
}

export interface OgcFeatureOlMapColors {
  stroke: string;
  selected: string;
}

export interface OgcGeoJsonFeature {
  type?: string;
  id?: string | number;
  geometry?: unknown;
  properties?: Record<string, unknown> | null;
}

export interface OgcFeatureOlMapOptions {
  target: HTMLElement;
  colors: OgcFeatureOlMapColors;
  onDrawn: (geometry: OgcDrawnGeometry) => void;
  onFeatureClick: (feature: OgcGeoJsonFeature) => void;
  onDrawModeChange?: (mode: OgcDrawMode | null) => void;
  /** Same GeoJSON canvas uses: dim outside the region and stroke the boundary. */
  boundaryGeoJsonUrl?: string;
}

/** CRS84 bbox from a Web Mercator extent. */
export function ogcBboxFromExtent3857(
  extent: Extent,
): [number, number, number, number] | null {
  const [west, south, east, north] = transformExtent(
    extent,
    'EPSG:3857',
    'EPSG:4326',
  );
  if (![west, south, east, north].every((n) => Number.isFinite(n))) return null;
  if (west >= east || south >= north) return null;
  return [west, south, east, north];
}

export function drawnGeometryFromOlGeometry(
  mode: OgcDrawMode,
  geometry: Geometry | undefined,
): OgcDrawnGeometry | null {
  if (!geometry) return null;
  const extent = geometry.getExtent();
  if (!extent) return null;
  const bbox = ogcBboxFromExtent3857(extent);
  if (!bbox) return null;

  if (mode === 'circle' && geometry instanceof CircleGeom) {
    const [lng, lat] = toLonLat(geometry.getCenter());
    return {
      type: 'circle',
      bbox,
      center: [lat, lng],
      radius: geometry.getRadius(),
    };
  }

  if (geometry instanceof Polygon) {
    const ring = geometry.getCoordinates()[0] ?? [];
    const coordinates = ring.map((coord) => {
      const [lng, lat] = toLonLat(coord);
      return [lat, lng] as [number, number];
    });
    return {
      type: mode === 'circle' ? 'rectangle' : mode,
      bbox,
      coordinates,
    };
  }

  return { type: mode, bbox };
}

/**
 * OpenLayers map for the OGC Feature Collection Get Data popup.
 * Matches canvas (OL Draw + createBox) instead of Leaflet.
 */
export class OgcFeatureOlMap {
  private readonly map: OlMap;
  private readonly drawLayer: VectorLayer<VectorSource>;
  private readonly drawSource = new VectorSource();
  private readonly pointSource = new VectorSource();
  private readonly shapeSource = new VectorSource();
  private readonly geoJson = new GeoJSON();
  private readonly listeners: EventsKey[] = [];
  private readonly sizeTimers: number[] = [];
  private draw: Draw | null = null;
  private selected: OgcGeoJsonFeature | null = null;
  private readonly pinCache = new Map<string, Style>();
  private destroyed = false;

  constructor(private readonly options: OgcFeatureOlMapOptions) {
    const india3857 = transformExtent(INDIA_MAP_EXTENT, 'EPSG:4326', 'EPSG:3857');
    this.drawLayer = new VectorLayer({
      source: this.drawSource,
      zIndex: 20,
      style: this.shapeStyle(false),
    });
    const shapeLayer = new VectorLayer({
      source: this.shapeSource,
      zIndex: 15,
      style: (feat) => this.shapeStyle(this.isSelected(feat)),
    });
    const clusterSource = new Cluster({
      distance: 40,
      source: this.pointSource,
    });
    const clusterLayer = new VectorLayer({
      source: clusterSource,
      zIndex: 16,
      style: (feat) => this.clusterStyle(feat),
    });

    this.map = new OlMap({
      target: options.target,
      view: new View({
        center: fromLonLat(DEFAULT_CENTER_LON_LAT),
        zoom: DEFAULT_ZOOM,
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM,
        extent: india3857,
        showFullExtent: true,
      }),
      controls: defaultControls({ zoom: false, rotate: false }).extend([
        new Zoom({}),
      ]),
      layers: [
        new TileLayer({
          source: new OSM({ crossOrigin: 'anonymous' }),
        }),
        this.drawLayer,
        shapeLayer,
        clusterLayer,
      ],
    });

    this.listen(this.map.on('click', (evt) => this.onMapClick(evt.pixel)));
    this.listen(
      this.map.on('pointermove', (evt) => {
        const hit = this.map.hasFeatureAtPixel(evt.pixel, {
          layerFilter: (layer) => this.isInteractiveLayer(layer),
          hitTolerance: 6,
        });
        const el = this.map.getTargetElement();
        if (el) el.style.cursor = hit ? 'pointer' : '';
      }),
    );

    for (const ms of [100, 300, 600]) {
      this.sizeTimers.push(window.setTimeout(() => this.updateSize(), ms));
    }

    if (options.boundaryGeoJsonUrl) {
      void this.loadBoundaryHighlight(options.boundaryGeoJsonUrl);
    }
  }

  private listen(key: EventsKey | EventsKey[]): void {
    if (Array.isArray(key)) this.listeners.push(...key);
    else this.listeners.push(key);
  }

  startDraw(mode: OgcDrawMode): void {
    this.stopDraw();
    let type: GeometryType = 'Polygon';
    let geometryFunction: ReturnType<typeof createBox> | undefined;
    if (mode === 'rectangle') {
      type = 'Circle';
      geometryFunction = createBox();
    } else if (mode === 'circle') {
      type = 'Circle';
    }

    const draw = new Draw({
      source: this.drawSource,
      type,
      stopClick: true,
      ...(geometryFunction ? { geometryFunction, maxPoints: 2 } : {}),
    });
    draw.on('drawstart', () => this.drawSource.clear());
    draw.on('drawend', (evt) => {
      const drawn = drawnGeometryFromOlGeometry(mode, evt.feature.getGeometry());
      this.map.removeInteraction(draw);
      if (this.draw === draw) this.draw = null;
      this.options.onDrawModeChange?.(null);
      if (drawn) this.options.onDrawn(drawn);
    });
    this.map.addInteraction(draw);
    this.draw = draw;
    this.options.onDrawModeChange?.(mode);
  }

  stopDraw(): void {
    if (!this.draw) return;
    this.draw.abortDrawing();
    this.map.removeInteraction(this.draw);
    this.draw = null;
    this.options.onDrawModeChange?.(null);
  }

  displayFeatures(features: OgcGeoJsonFeature[]): void {
    this.clearFeatures();
    for (const feature of features) {
      this.addFeature(feature);
    }
    this.fitToFeatures();
  }

  setSelected(feature: OgcGeoJsonFeature | null): void {
    this.selected = feature;
    this.pointSource.changed();
    this.shapeSource.changed();
  }

  clearDrawn(): void {
    this.drawSource.clear();
  }

  clearFeatures(): void {
    this.selected = null;
    this.pointSource.clear();
    this.shapeSource.clear();
  }

  resetView(): void {
    this.map.getView().setCenter(fromLonLat(DEFAULT_CENTER_LON_LAT));
    this.map.getView().setZoom(DEFAULT_ZOOM);
  }

  updateSize(): void {
    this.map.updateSize();
  }

  destroy(): void {
    this.destroyed = true;
    for (const t of this.sizeTimers) window.clearTimeout(t);
    this.sizeTimers.length = 0;
    this.stopDraw();
    for (const key of this.listeners) unByKey(key);
    this.listeners.length = 0;
    this.map.setTarget(undefined);
  }

  private isOverlayLayer(layer: BaseLayer): boolean {
    return Boolean(
      layer.get(CV_MAHARASHTRA_BOUNDARY_KEY) || layer.get(CV_MAHARASHTRA_DIM_KEY),
    );
  }

  private isInteractiveLayer(layer: BaseLayer): boolean {
    return layer !== this.drawLayer && !this.isOverlayLayer(layer);
  }

  private async loadBoundaryHighlight(boundaryGeoJsonUrl: string): Promise<void> {
    const projection = 'EPSG:3857';
    try {
      const dimLayer = await createMaharashtraOutsideDimLayer(
        boundaryGeoJsonUrl,
        projection,
      );
      const boundaryLayer = await createMaharashtraBoundaryLayer(
        boundaryGeoJsonUrl,
        projection,
      );
      if (this.destroyed) return;
      dimLayer.setZIndex(5);
      boundaryLayer.setZIndex(6);
      const layers = this.map.getLayers();
      layers.insertAt(1, dimLayer);
      layers.insertAt(2, boundaryLayer);
    } catch (err) {
      console.error('OGC map boundary overlay failed to load', err);
    }
  }

  private onMapClick(pixel: number[]): void {
    const feature = this.map.forEachFeatureAtPixel(
      pixel,
      (feat, layer) => {
        if (!layer || !this.isInteractiveLayer(layer)) return undefined;
        return feat;
      },
      { hitTolerance: 8 },
    );
    if (!feature) return;

    const clustered = feature.get('features') as Feature[] | undefined;
    if (clustered?.length) {
      if (clustered.length === 1) {
        this.emitFeature(clustered[0]);
        return;
      }
      const coords = clustered
        .map((f) => f.getGeometry())
        .filter((g): g is Point => g instanceof Point)
        .map((g) => g.getCoordinates());
      if (coords.length) {
        this.map.getView().fit(boundingExtent(coords), {
          padding: [40, 40, 40, 40],
          duration: 250,
          maxZoom: 16,
        });
      }
      return;
    }

    this.emitFeature(feature);
  }

  private emitFeature(feature: FeatureLike): void {
    const ogc = feature.get(OGC_FEATURE_KEY) as OgcGeoJsonFeature | undefined;
    if (ogc) this.options.onFeatureClick(ogc);
  }

  private addFeature(feature: OgcGeoJsonFeature): void {
    const geom = asGeoJsonGeometry(feature.geometry);
    const geomType = geom?.type;
    if (!geomType || !geom) return;

    try {
      const olFeature = this.geoJson.readFeature(
        { type: 'Feature', geometry: geom, properties: {} },
        { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
      );
      const feats = Array.isArray(olFeature) ? olFeature : [olFeature];
      for (const f of feats) {
        if (!f?.getGeometry()) continue;
        f.set(OGC_FEATURE_KEY, feature);
        if (geomType === 'Point') this.pointSource.addFeature(f);
        else this.shapeSource.addFeature(f);
      }
    } catch {
      const lonLat = firstLonLat(geom);
      if (!lonLat) return;
      const f = new Feature(new Point(fromLonLat(lonLat)));
      f.set(OGC_FEATURE_KEY, feature);
      this.pointSource.addFeature(f);
    }
  }

  private fitToFeatures(): void {
    const extent = createEmpty();
    if (this.pointSource.getFeatures().length) {
      const pointExtent = this.pointSource.getExtent();
      if (pointExtent) extendExtent(extent, pointExtent);
    }
    if (this.shapeSource.getFeatures().length) {
      const shapeExtent = this.shapeSource.getExtent();
      if (shapeExtent) extendExtent(extent, shapeExtent);
    }
    if (isEmpty(extent)) return;
    this.map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 14, duration: 250 });
  }

  private isSelected(feat: FeatureLike): boolean {
    const ogc = feat.get(OGC_FEATURE_KEY) as OgcGeoJsonFeature | undefined;
    if (ogc && ogc === this.selected) return true;
    const clustered = feat.get('features') as Feature[] | undefined;
    return clustered?.length === 1
      ? clustered[0].get(OGC_FEATURE_KEY) === this.selected
      : false;
  }

  private clusterStyle(feat: FeatureLike): Style {
    const clustered = (feat.get('features') as Feature[] | undefined) ?? [];
    if (clustered.length > 1) {
      const { stroke } = this.options.colors;
      return new Style({
        image: new CircleStyle({
          radius: 20,
          fill: new Fill({ color: stroke }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
        text: new Text({
          text: String(clustered.length),
          fill: new Fill({ color: '#fff' }),
          font: '700 13px sans-serif',
        }),
      });
    }
    const inner = clustered[0] ?? feat;
    return this.pinStyle(this.isSelected(inner));
  }

  private shapeStyle(selected: boolean): Style {
    const color = selected ? this.options.colors.selected : this.options.colors.stroke;
    return new Style({
      stroke: new Stroke({ color, width: selected ? 3 : 2 }),
      fill: new Fill({ color: hexToRgba(color, 0.28) }),
    });
  }

  private pinStyle(selected: boolean): Style {
    const color = selected ? this.options.colors.selected : this.options.colors.stroke;
    let style = this.pinCache.get(color);
    if (!style) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="42"><path d="M12 4C9.24 4 7 6.24 7 9c0 2.85 2.92 7.21 5 9.88 2.11-2.69 5-7 5-9.88 0-2.76-2.24-5-5-5zm0 7.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${color}" stroke="white" stroke-width="0.5"/><circle cx="12" cy="9.5" r="2" fill="white"/></svg>`;
      style = new Style({
        image: new Icon({
          src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
          anchor: [0.5, 1],
          width: 30,
          height: 42,
        }),
      });
      this.pinCache.set(color, style);
    }
    return style;
  }
}

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const n = Number.parseInt(full, 16);
  if (!Number.isFinite(n)) return `rgba(13, 92, 66, ${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function asGeoJsonGeometry(
  geom: unknown,
): { type?: string; coordinates?: unknown } | null {
  if (!geom || typeof geom !== 'object') return null;
  return geom as { type?: string; coordinates?: unknown };
}

function firstLonLat(geom: {
  type?: string;
  coordinates?: unknown;
}): [number, number] | null {
  const coords = geom.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  if (geom.type === 'Point' && typeof coords[0] === 'number') {
    const [lng, lat] = coords as number[];
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  }
  let cursor: unknown = coords;
  while (Array.isArray(cursor) && Array.isArray(cursor[0])) cursor = cursor[0];
  if (Array.isArray(cursor) && typeof cursor[0] === 'number') {
    const [lng, lat] = cursor as number[];
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  }
  return null;
}
